import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import MarketingNavbar from "../marketingNav";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { analytics, auth, db, getUserPlanData, remoteConfig } from "../../services/firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createDesignFromTemplate } from "../../services/createDesignFromTemplate";
import {
  PENDING_TEMPLATE_STORAGE_KEY,
  templateIdFromTemplatesPath,
} from "../../utils/navigation";
import { logEvent } from "../../services/evalMode.js";
import { EDITOR_ONBOARDING_STORAGE_COMPLETED } from "../onboarding/editorOnboardingSteps.tsx";
import {
  getDefaultTemplateIdForRole,
  isBeginnerPriorExperience,
  isLargeEnsembleSize,
  type OnboardingRole,
} from "./onboardingFlowConfig.ts";
import { logActivationDrillCreated } from "../../analytics/onboarding.ts";

interface OnboardingLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  canProgress?: boolean;
  flowId: string;
  experimentName: string;
  experimentVariant: string;
  fastPath: boolean;
}

function buildEditorUrl(
  drillId: string,
  opts: {
    tutorial: boolean;
    onboardingRole: string | null;
    trial: boolean;
    priorExperience?: string | null;
    typicalPerformerCount?: string | null;
  }
) {
  const q = new URLSearchParams();
  if (opts.tutorial) q.set("tutorial", "1");
  if (opts.onboardingRole) q.set("onboarding_role", opts.onboardingRole);
  if (opts.trial) q.set("onboarding_trial", "1");
  if (isBeginnerPriorExperience(opts.priorExperience)) {
    q.set("onboarding_experience", "beginner");
  }
  if (isLargeEnsembleSize(opts.typicalPerformerCount)) {
    q.set("onboarding_ensemble", "large");
  }
  const s = q.toString();
  return `/designs/${drillId}${s ? `?${s}` : ""}`;
}

export function OnboardingLayout({
  children,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  canProgress = true,
  flowId,
  experimentName,
  experimentVariant,
  fastPath,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isFinishing, setIsFinishing] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
      } else {
        navigate("/login"); // Or redirect to login
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth, navigate]); // Include `navigate` in dependencies

  const exitOnboarding = useCallback(async () => {
    logEvent(analytics, "complete_account_setup", {
      user: auth.currentUser?.uid,
      flow_id: flowId,
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
      fast_path: fastPath,
    });

    const uid = auth.currentUser?.uid;
    if (!uid) {
      navigate("/login");
      return;
    }

    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.exists() ? userSnap.data() : {};
    const onboardingRole = (userData.onboardingRole ||
      null) as OnboardingRole | null;

    const plan = await getUserPlanData(uid);
    const showTrialCta = !plan.planActive;

    const finalizeUserOnboarding = async () => {
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return;
      const userData = userDoc.data();
      const currentChecklist = userData.onboardingChecklist || {};
      await updateDoc(userRef, {
        onboarded: "Y",
        onboardingWizardCompletedAt: serverTimestamp(),
        onboardingPathId: flowId,
        onboardingExperimentName: experimentName,
        onboardingExperimentVariant: experimentVariant,
        onboardingFastPath: fastPath,
        onboardingDeferredProfilingPending: fastPath === true,
        onboardingChecklist: {
          ...currentChecklist,
          "create-drill": true,
        },
      });
    };

    const redirectParam = searchParams.get("redirect");
    let templateId: string | null = null;
    if (redirectParam) {
      templateId = templateIdFromTemplatesPath(redirectParam);
    }
    if (!templateId && typeof sessionStorage !== "undefined") {
      templateId = sessionStorage.getItem(PENDING_TEMPLATE_STORAGE_KEY);
    }
    if (!templateId) {
      const roleTemplate = getDefaultTemplateIdForRole(
        remoteConfig,
        onboardingRole
      );
      templateId = roleTemplate || null;
    }

    const drillTitle =
      (userData.onboardingProfile?.firstShowTitle || "").trim() ||
      "Untitled Drill Design";
    const pendingId = userData.pendingFirstDesignId as string | undefined;

    const editorOnboardingDone =
      localStorage.getItem(EDITOR_ONBOARDING_STORAGE_COMPLETED) === "true";

    const navigateToDrill = (
      drillId: string,
      fromTemplate: boolean,
      hadNoDrillsForTutorial: boolean
    ) => {
      logActivationDrillCreated({
        flow_id: flowId,
        document_id: drillId,
        from_template: fromTemplate,
        onboarding_role: onboardingRole,
      });
      logEvent(analytics, "create_new_drill_document", {
        drill_title: drillTitle,
        created_by: uid,
        created_at: Timestamp.now().toDate().toISOString(),
        document_id: drillId,
        device_type: navigator.userAgent.includes("Mobi") ? "mobile" : "desktop",
      });
      const tutorial = hadNoDrillsForTutorial && !editorOnboardingDone;
      navigate(
        buildEditorUrl(drillId, {
          tutorial,
          onboardingRole: onboardingRole || null,
          trial: showTrialCta,
          priorExperience:
            userData.experience || userData.onboardingProfile?.priorExperience,
          typicalPerformerCount:
            userData.typicalPerformerCount ||
            userData.onboardingProfile?.typicalPerformerCount,
        })
      );
    };

    if (templateId) {
      setIsFinishing(true);
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(PENDING_TEMPLATE_STORAGE_KEY);
        }
        const newId = await createDesignFromTemplate(
          templateId,
          uid,
          drillTitle
        );
        await finalizeUserOnboarding();
        navigateToDrill(newId, true, true);
      } catch (e) {
        console.error("Error creating drill from template after onboarding:", e);
        navigate("/my-designs");
      } finally {
        setIsFinishing(false);
      }
      return;
    }

    setIsFinishing(true);
    try {
      if (pendingId) {
        await updateDoc(doc(db, "designs", pendingId), {
          title: drillTitle,
          updated: Timestamp.now(),
        });
        await finalizeUserOnboarding();
        navigateToDrill(pendingId, false, true);
        return;
      }

      const designsQuery = query(
        collection(db, "designs"),
        where("createdBy", "==", uid)
      );
      const existing = await getDocs(designsQuery);
      const hadNoDrills = existing.empty;

      const newDrill = {
        title: drillTitle,
        createdAt: Timestamp.now(),
        createdBy: uid,
        updated: Timestamp.now(),
      };
      const docRef = await addDoc(collection(db, "designs"), newDrill);

      await finalizeUserOnboarding();
      navigateToDrill(docRef.id, false, hadNoDrills);
    } catch (e) {
      console.error("Error creating drill after onboarding:", e);
      navigate("/my-designs");
    } finally {
      setIsFinishing(false);
    }
  }, [
    navigate,
    searchParams,
    flowId,
    experimentName,
    experimentVariant,
    fastPath,
  ]);

  return (
    <>
      {/* <MarketingNavbar /> */}
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl min-h-[600px] relative overflow-hidden">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
            <div
              className="h-full bg-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>

          <div className="p-8 flex flex-col h-full">
            {/* Content */}
            <div className="flex-1">{children}</div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={onBack}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors
                ${currentStep === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </button>

              <div className="text-sm text-gray-500">
                Step {currentStep} of {totalSteps}
              </div>

              <button
                type="button"
                onClick={currentStep === totalSteps ? exitOnboarding : onNext}
                disabled={!canProgress || isFinishing}
                className={`flex items-center px-6 py-2 rounded-lg transition-all
                ${canProgress && !isFinishing
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
              >
                {currentStep === totalSteps && isFinishing
                  ? "Opening editor…"
                  : currentStep === totalSteps
                    ? "Get Started"
                    : "Next"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
